import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { csvToContacts, countWithEmail } from "@/lib/csv";

// CSV contact import for a campaign's audience.
// - If `campaignId` is provided, imports directly via the API.
// - Else (builder, campaign not created yet), calls `onParsed(contacts)` to stash them.
export function CampaignContactImport({ campaignId, onImported, onParsed }) {
  const fileRef = useRef(null);
  const [contacts, setContacts] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const text = await f.text();
      const parsed = csvToContacts(text);
      if (!parsed.length) {
        toast.error("No contacts found — check the CSV has a header row (Company Name, Email, …)");
        return;
      }
      setContacts(parsed);
      if (onParsed) onParsed(parsed, f.name);
      toast.success(`${parsed.length} contacts parsed — review then import`);
    } catch (err) {
      toast.error("Could not read this CSV file");
    }
    e.target.value = "";
  };

  const clear = () => {
    setContacts(null);
    setFileName("");
    if (onParsed) onParsed(null, "");
  };

  const doImport = async () => {
    if (!contacts?.length || !campaignId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/import-contacts`, { contacts });
      toast.success(
        `Imported ${data.created} new${data.reused ? ` + ${data.reused} existing` : ""} · ${data.enrolled} enrolled${data.no_email ? ` · ${data.no_email} without email` : ""}`
      );
      clear();
      onImported?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const withEmail = contacts ? countWithEmail(contacts) : 0;

  return (
    <div data-testid="campaign-csv-import">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onFile}
        className="hidden"
        data-testid="campaign-csv-input"
      />
      {!contacts ? (
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={() => fileRef.current?.click()}
          data-testid="campaign-csv-pick-btn"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" /> Import contacts (CSV)
        </Button>
      ) : (
        <div className="flex flex-wrap items-center gap-3 rounded-sm border border-signal-500/40 bg-signal-500/5 px-3 py-2.5">
          <FileSpreadsheet className="w-4 h-4 text-signal-500 shrink-0" />
          <div className="text-xs min-w-0">
            <div className="font-medium truncate" data-testid="campaign-csv-filename">{fileName}</div>
            <div className="text-muted-foreground">
              {contacts.length} contacts · <span className="text-emerald-500">{withEmail} emailable</span>
              {contacts.length - withEmail > 0 && <span> · {contacts.length - withEmail} without email</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {campaignId && (
              <Button size="sm" className="rounded-sm" onClick={doImport} disabled={busy} data-testid="campaign-csv-import-btn">
                <Check className="w-3.5 h-3.5 mr-1.5" />
                {busy ? "Importing…" : `Import ${contacts.length}`}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clear} data-testid="campaign-csv-clear-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignContactImport;
